
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, CheckCircle, AlertCircle, Info, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotifications, Notification } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { currentUser } = useAuth();
  const { notifications, markAsRead, markAllAsRead, loading, fetchNotifications } = useNotifications();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    fetchNotifications();
  }, [currentUser, navigate, fetchNotifications]);

  // Helper to get icon based on notification type
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  // Filter notifications
  const allNotifications = [...notifications].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  
  const unreadNotifications = allNotifications.filter(n => !n.isRead);
  
  // Render a notification item
  const renderNotificationItem = (notification: Notification) => (
    <Card 
      key={notification.id}
      className={cn(
        "mb-4 transition-all hover:shadow-md cursor-pointer",
        !notification.isRead && (isDark ? "border-blue-500/30 shadow-blue-500/5" : "border-blue-200"),
        isDark ? "bg-[#151830]/90 hover:bg-[#1a1f3a]" : "bg-white hover:bg-slate-50"
      )}
      onClick={() => {
        if (!notification.isRead) {
          markAsRead(notification.id);
        }
        if (notification.link) {
          navigate(notification.link);
        }
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="mt-1">
            {getNotificationIcon(notification.type)}
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <h4 className="font-medium">{notification.title}</h4>
              {!notification.isRead && (
                <span className="h-2 w-2 rounded-full bg-blue-500 mt-1"></span>
              )}
            </div>
            <p className="text-sm mt-1">{notification.message}</p>
            <span className="text-xs text-muted-foreground mt-2 block">
              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="container max-w-4xl py-8 px-4 md:px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Bell className="mr-2 h-6 w-6" />
            <h1 className="text-2xl font-bold">Notifications</h1>
          </div>
          
          {unreadNotifications.length > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={markAllAsRead}
            >
              Mark all as read
            </Button>
          )}
        </div>
        
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="all">
              All ({allNotifications.length})
            </TabsTrigger>
            <TabsTrigger value="unread">
              Unread ({unreadNotifications.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : allNotifications.length > 0 ? (
              <div>
                {allNotifications.map(renderNotificationItem)}
              </div>
            ) : (
              <Card className={cn(
                "bg-muted/30 border-dashed",
                isDark ? "border-slate-700/30" : "border-slate-200"
              )}>
                <CardContent className="py-10 flex flex-col items-center justify-center text-center">
                  <MessageCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No notifications yet</h3>
                  <p className="text-muted-foreground max-w-md">
                    You'll see notifications about important updates, messages, and other activities here.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="unread">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : unreadNotifications.length > 0 ? (
              <div>
                {unreadNotifications.map(renderNotificationItem)}
              </div>
            ) : (
              <Card className={cn(
                "bg-muted/30 border-dashed",
                isDark ? "border-slate-700/30" : "border-slate-200"
              )}>
                <CardContent className="py-10 flex flex-col items-center justify-center text-center">
                  <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">All caught up!</h3>
                  <p className="text-muted-foreground max-w-md">
                    You have no unread notifications at the moment.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
};

export default NotificationsPage;
